import axios, { AxiosError, AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { ApiResponse, ApiErrorType, API_STATUS_CODES, RequestMetadata } from '../../../types/api';
import { HttpClientConfig, HttpRequestConfig } from './types';
import { logNetworkError, logNetworkRequest } from '../../../utils/logging';

export class HttpError extends Error {
  constructor(
    public readonly type: ApiErrorType,
    public readonly statusCode: number,
    public readonly response?: ApiResponse,
    message?: string,
    public readonly context?: string
  ) {
    // Use translation key instead of hardcoded message
    super(getErrorTranslationKey(statusCode, context));
    this.name = 'HttpError';
  }
}

// Map HTTP status codes to translation keys
function getErrorTranslationKey(status: number, context?: string): string {
  // If we have a context and it's a 404, try to use context-specific message
  if (status === 404 && context) {
    return `${context}.notFound`;
  }

  switch (status) {
    case 400:
      return 'errors.badRequest';
    case 401:
      return 'errors.unauthorized';
    case 403:
      return 'errors.forbidden';
    case 404:
      return 'errors.notFound';
    case 409:
      return 'errors.conflict';
    case 413:
      return 'errors.fileTooLarge';
    case 422:
      return 'errors.validation';
    case 429:
      return 'errors.tooManyRequests';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'errors.serverError';
    default:
      return 'errors.somethingWentWrong';
  }
}

export class HttpClient {
  private instance: AxiosInstance;
  private abortControllers: Map<string, AbortController>;
  private metadata: Record<string, unknown> = {};
  private context?: string;

  constructor(config: HttpClientConfig) {
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...this.getDefaultHeaders(),
        ...config.headers,
      },
    });

    this.abortControllers = new Map();
    this.setupInterceptors();
    this.setupRetry(config.retries);

    // Add request interceptor for logging
    this.instance.interceptors.request.use(
      (config) => {
        logNetworkRequest(config.url || '', config.method || 'unknown', config.headers);
        return config;
      },
      (error) => {
        logNetworkError(error, error?.config?.url || 'unknown');
        return Promise.reject(error);
      }
    );

    // Add response interceptor for logging errors
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        logNetworkError(error, error?.config?.url || 'unknown');
        return Promise.reject(error);
      }
    );
  }

  // Set the context for error messages (e.g., 'video', 'search', etc.)
  public setContext(context: string): void {
    this.context = context;
  }

  private getDefaultHeaders(): Record<string, string> {
    return {
      'Accept-Language': navigator.language,
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'X-Client-Version': import.meta.env.VITE_APP_VERSION || '1.0.0',
    };
  }

  private setupInterceptors(): void {
    this.instance.interceptors.response.use(
      (response) => {
        const apiResponse = response.data as ApiResponse;
        if (apiResponse.statusCode !== API_STATUS_CODES.SUCCESS) {
          throw new HttpError(
            'DefaultResponse',
            response.status,
            apiResponse,
            undefined,
            this.context
          );
        }
        return response;
      },
      (error: AxiosError) => {
        if (!error.response) {
          throw new HttpError(
            'DefaultResponse',
            0,
            undefined,
            'errors.networkError',
            this.context
          );
        }

        const errorType = this.getErrorType(error.response.status);
        throw new HttpError(
          errorType,
          error.response.status,
          error.response.data as ApiResponse,
          undefined,
          this.context
        );
      }
    );
  }

  private setupRetry(retries?: number): void {
    if (retries) {
      axiosRetry(this.instance, {
        retries: retries,
        retryDelay: (retryCount) => {
          // Exponential backoff with jitter
          const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
          return delay + Math.random() * 1000;
        },
        retryCondition: (error: AxiosError) => {
          // Don't retry on 429 (Too Many Requests)
          if (error.response?.status === 429) {
            return false;
          }
          
          // Only retry on network errors, 5xx errors, and idempotent requests
          return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
            (error.response?.status ? error.response.status >= 500 : false);
        },
      });
    }
  }

  private getErrorType(status: number): ApiErrorType {
    switch (status) {
      case 400: return 'BadRequest';
      case 401: return 'Unauthorized';
      case 403: return 'PermissionDenied';
      case 404: return 'NotFound';
      case 409: return 'Conflict';
      case 413: return 'TooLarge';
      case 422: return 'Unprocessable';
      default: return 'DefaultResponse';
    }
  }

  private getRequestKey(method: string, url: string): string {
    return `${method}-${url}`;
  }

  private getAbortController(method: string, url: string): AbortController {
    const key = this.getRequestKey(method, url);
    const existingController = this.abortControllers.get(key);
    
    if (existingController) {
      existingController.abort();
    }

    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    return controller;
  }

  public setMetadata(metadata: Partial<RequestMetadata>): void {
    this.metadata = { ...this.metadata, ...metadata };
  }

  public setAuthToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  public clearAuthToken(): void {
    delete this.instance.defaults.headers.common.Authorization;
  }

  private async request<T>(
    method: string,
    url: string,
    config: HttpRequestConfig = {},
    data?: unknown
  ): Promise<T> {
    const controller = this.getAbortController(method, url);
    
    try {
      const response = await this.instance.request<ApiResponse<T>>({
        method,
        url,
        data,
        ...config,
        signal: controller.signal,
      });

      return response.data.data as T;
    } finally {
      this.abortControllers.delete(this.getRequestKey(method, url));
    }
  }

  public async get<T>(url: string, config?: HttpRequestConfig): Promise<T> {
    return this.request<T>('GET', url, config);
  }

  public async post<T>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    return this.request<T>('POST', url, config, data);
  }

  public async put<T>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    return this.request<T>('PUT', url, config, data);
  }

  public async patch<T>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, config, data);
  }

  public async delete<T>(url: string, config?: HttpRequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, config);
  }

  public cancelRequest(method: string, url: string): void {
    const key = this.getRequestKey(method, url);
    const controller = this.abortControllers.get(key);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(key);
    }
  }

  public cancelAllRequests(): void {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
  }
} 